import React, { Component } from 'react';
import moment from 'moment';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';

import { InputGroup, Switch, Select } from 'core/form';

import {
  ALERT_LOOKBACK_SHORTCUTS,
  ALERT_STATE_LABELS,
  ALERT_ACKSTATE_LABELS,
  ALERTING_EXPORT_PER_PAGE_COUNT_DEFAULT,
  EXTERNAL_SYSTEMS
} from '@kentik/ui-shared/alerting/constants';

import { parseQueryString } from 'app/util/utils';
import { timezone } from 'core/util/dateUtils';
import AdminFilterSidebar from 'app/components/admin/AdminFilterSidebar';
import { SEVERITY_OPTIONS } from 'app/views/alerting/components/severityOptions';
import AlertStatusTag from 'app/views/alerting/components/AlertStatusTag';
import { rejectInvalidAlertIds } from 'app/views/alerting/util/alertIdSearch';

@inject('$alerting', '$app', '$mkp', '$metrics', '$sites', '$mitigations')
@withRouter
@observer
class AlertingFilterSidebar extends Component {
  static defaultProps = {
    handleTotalCounts: false,
    lookbackOnly: false,
    readOnly: false,
    filterValues: undefined,
    updateHashOnFilterChange: true
  };

  state = { policyOptions: [], showTenantAlerts: false };

  componentDidMount() {
    const { $app, $mkp, $alerting } = this.props;

    if (!$app.isSubtenant) {
      $mkp.tenants.fetch();
    }

    $alerting.policyCollection.fetch().then(() => {
      this.setState({ policyOptions: $alerting.policyRuleIdOptions });
    });
  }

  get routingFilters() {
    const { location } = this.props;
    const queryStringFilters = location?.search ? parseQueryString(location.search) : {};
    const locationStateFilters = location?.state?.filters || {};

    return Object.assign({}, queryStringFilters, locationStateFilters);
  }

  get queryAlertId() {
    const { match } = this.props;

    return match?.params.alertId;
  }

  get resetValues() {
    return {
      keyPartsSearch: '',
      sites: [],
      alarmIds: '',
      mitigationIds: [],
      applications: [],
      severities: [],
      includeSubpolicies: false,
      ackStates: [],
      lookback: 24 * 3600,
      ruleIds: [],
      tenants: [],
      states: ['alarm'],
      contextSearch: undefined,
      hasAiInvestigation: false,
      externalContextSystems: []
    };
  }

  get filterFields() {
    const { $alerting, $app, $mkp, $sites, filterValues, lookbackOnly } = this.props;

    const { policyOptions, showTenantAlerts } = this.state;
    const filterOverrides = {};
    const locationFilters = this.routingFilters;
    const passedFilters = Object.assign({}, filterValues, locationFilters, filterOverrides);

    const filterFields = {
      contextSearch: {
        name: 'contextSearch',
        hidden: true,
        defaultValue: this.resetValues.contextSearch
      },
      daterange: {
        name: 'daterange',
        label: `Time Range (${timezone.value})`,
        isDateRange: true,
        hidden: lookbackOnly,
        closeDateRangeOnSelection: true,
        startField: 'startDate',
        endField: 'endDate',
        asStrings: true,
        startDefaultValue: moment().subtract(1, 'd'),
        endDefaultValue: moment(),
        adjustEndTime: true,
        showShortcutLabelAsInterval: true,
        overrideShortcuts: ALERT_LOOKBACK_SHORTCUTS
      },

      lookback: {
        name: 'lookback',
        label: 'Time Range',
        defaultValue: this.resetValues.lookback,
        hidden: !lookbackOnly,
        customField: <Select fill />,
        options: [
          { label: 'Last 24 hours', value: 24 * 3600 },
          { label: 'Last 7 days', value: 24 * 7 * 3600 },
          { label: 'Last 14 days', value: 24 * 14 * 3600 },
          { label: 'Last 30 days', value: 24 * 30 * 3600 },
          { label: 'Last 90 days', value: 24 * 90 * 3600 }
        ]
      },

      applications: {
        name: 'applications',
        label: 'Type',
        defaultValue: this.resetValues.applications,
        allowMultiSelect: true,
        showCheckboxes: true,
        options: $alerting.policyTypeOptions
      },

      states: {
        name: 'states',
        label: 'Alert State',
        allowMultiSelect: true,
        showCheckboxes: true,
        defaultValue: this.resetValues.states,
        options: ['alarm', 'clear'].map((value) => ({
          value,
          label: ALERT_STATE_LABELS[value],
          displayLabel: <AlertStatusTag isActive={value === 'alarm'} />
        }))
      },

      ackStates: {
        name: 'ackStates',
        label: 'Ack State',
        allowMultiSelect: true,
        showCheckboxes: true,
        defaultValue: this.resetValues.ackStates,
        options: [
          'ALARM_ACKNOWLEDGEMENT_REQUIRED',
          'ALARM_ACKNOWLEDGEMENT_DONE',
          'ALARM_ACKNOWLEDGEMENT_NOT_ACKED',
          'ACKED_BY_ME'
        ].map((value) => ({
          value,
          label: ALERT_ACKSTATE_LABELS[value]
        }))
      },

      severities: {
        name: 'severities',
        label: 'Severity',
        defaultValue: this.resetValues.severities,
        allowMultiSelect: true,
        showCheckboxes: true,
        options: SEVERITY_OPTIONS
      },

      alarmIds: {
        name: 'alarmIds',
        label: 'Alert ID',
        defaultValue: this.resetValues.alarmIds,
        customField: <InputGroup />,
        beforeOnChange: rejectInvalidAlertIds
      },

      sites: {
        name: 'sites',
        label: 'Sites',
        options: $sites.siteOptions,
        hidden: $app.isSubtenant,
        allowMultiSelect: true,
        placeholder: 'No sites selected',
        defaultValue: this.resetValues.sites
      },

      mitigationIds: {
        name: 'mitigationIds',
        label: 'Mitigation ID',
        hidden: true,
        placeholder: 'No mitigations selected',
        defaultValue: this.resetValues.mitigationIds
      },

      ruleIds: {
        name: 'ruleIds',
        label: 'Policies',
        options: policyOptions,
        defaultValue: [],
        allowMultiSelect: true,
        placeholder: 'No policies selected',
        valueRenderer: (option) => option.matcher
      },

      includeSubpolicies: {
        name: 'includeSubpolicies',
        defaultValue: this.resetValues.includeSubpolicies,
        customField: <Switch switchLabel="Include tenant alerts" />,
        hidden: $app.isSubtenant || $mkp.tenants.tenantAlertingOptions.length === 0
      },

      tenants: {
        name: 'tenants',
        label: 'Tenants',
        options: $mkp.tenants.tenantAlertingOptionsWithPolicies,
        hidden: !showTenantAlerts,
        defaultValue: this.resetValues.tenants,
        allowMultiSelect: true,
        placeholder: 'No tenants selected'
      },

      keyPartsSearch: {
        name: 'keyPartsSearch',
        label: 'Dimension Value',
        customField: <InputGroup />,
        defaultValue: this.resetValues.keyPartsSearch
      },

      hasAiInvestigation: {
        name: 'hasAiInvestigation',
        defaultValue: this.resetValues.hasAiInvestigation,
        customField: <Switch switchLabel="Has AI Investigation" />
      },

      externalContextSystems: {
        name: 'externalContextSystems',
        label: 'External Context',
        allowMultiSelect: true,
        showCheckboxes: true,
        defaultValue: this.resetValues.externalContextSystems,
        options: [
          { value: EXTERNAL_SYSTEMS.SERVICENOW, label: 'ServiceNow' },
          { value: EXTERNAL_SYSTEMS.JIRA_CLOUD, label: 'Jira' }
        ]
      }
    };

    if (passedFilters) {
      Object.keys(passedFilters).forEach((key) => {
        let defaultValue = passedFilters[key];
        const filterField = filterFields[key];

        // set the default form values for all of the incoming query values and prop values
        if (filterField) {
          if (filterField.allowMultiSelect && !Array.isArray(defaultValue)) {
            defaultValue = [defaultValue];
          }

          if (key === 'lookback') {
            defaultValue = parseInt(defaultValue) || 0;
            // if we're only showing lookback (and not start/end dates) make sure we have a valid value for the lookback (at least 24hrs)
            if (lookbackOnly && defaultValue < 86400) {
              defaultValue = 86400;
            }
          }

          filterField.defaultValue = defaultValue;
        } else {
          // could not find a matching filter form field for the incoming value - check start and end dates
          if (key === 'startDate' && !lookbackOnly) {
            filterFields.daterange.startDefaultValue = moment(defaultValue);
            filterFields.lookback.defaultValue = 0;
          }
          if (key === 'endDate' && !lookbackOnly) {
            filterFields.daterange.endDefaultValue = moment(defaultValue);
            filterFields.lookback.defaultValue = 0;
          }
        }
      });
    }

    if (this.queryAlertId) {
      filterFields.alarmIds.defaultValue = this.queryAlertId;
      filterFields.lookback.defaultValue = 7776000;
      filterFields.states.defaultValue = [];
      filterFields.applications.defaultValue = [];
    }

    return Object.values(filterFields);
  }

  get adjustedFilterValues() {
    const { filterValues, lookbackOnly } = this.props;

    // Prefer lookback for subscriptions and make sure its at least 24hrs
    if (lookbackOnly && filterValues?.lookback < 86400) {
      filterValues.lookback = 86400;
    }

    return filterValues;
  }

  handleFilterChange = (filters) => {
    const { $alerting, $app, collection, onChange, handleTotalCounts } = this.props;

    this.setState({ showTenantAlerts: !!filters.includeSubpolicies });

    if (!filters.includeSubpolicies) {
      filters.tenants = [];
    }

    // Context search value is stored elsewhere and not on the sidebar, so we pull the existing value before overwriting
    // In the case of subscriptions, there is no collection passed, so check before using context search.
    if (collection?.getServerFilter) {
      const { contextSearch } = collection.getServerFilter();
      filters.contextSearch = contextSearch;
    }

    if (onChange) {
      onChange(filters);
    }

    if (collection) {
      const promises = [
        collection.setServerFilter(
          filters,
          {},
          { data: { limit: $app.isExport ? ALERTING_EXPORT_PER_PAGE_COUNT_DEFAULT : undefined } }
        )
      ];

      if (handleTotalCounts) {
        promises.push(
          $alerting.handleTotalCountLoad({
            filters: { ...collection.getServerFilter(), lookback: filters.lookback },
            isExport: $app.isExport
          })
        );

        return Promise.all(promises).then(([results, totalsResults]) => {
          if (collection.groupBy && results && totalsResults) {
            collection.setPageState({
              groupedDataFracturedKey: collection.getGroupedDataFracturedKey(results, totalsResults)
            });
          }

          return [results, totalsResults];
        });
      }

      return Promise.all(promises);
    }

    return Promise.resolve();
  };

  render() {
    const { filterValues, lookbackOnly, readOnly, ...otherProps } = this.props;

    return (
      <AdminFilterSidebar
        {...otherProps}
        readOnly={readOnly}
        filterValues={this.adjustedFilterValues}
        filterFields={this.filterFields}
        resetValues={this.resetValues}
        onFilterChange={this.handleFilterChange}
      />
    );
  }
}

export default AlertingFilterSidebar;
